import { useAsyncData } from '@snapchat/async-data-browser';
import {
  businessPersonasEmployeeCountDisplayTextMap,
  businessPersonasPrimaryIndustryDisplayTextMap,
  businessPersonasRevenueDisplayTextMap,
} from '@snapchat/mw-contentful-schema';
import { useCallback, useContext, useState } from 'react';

import { AppContext } from '../AppContext';
import { logger } from '../helpers/logging';
import type { ProfileResponse } from '../server/endpoints/profile/getProfile';
import { getPersonasCookie } from '../utils/personas/personasUtils';

const component = 'useUserBusinessProfile';

type UserBusinessProfileDecition = {
  isThinking: boolean;
  /**
   * The decision of the user's business profile.
   *
   * - `undefined` if profile has not been fetched yet
   * - `null` if profile has been fetched but did not match (no revoptimal id)
   * - Empty array if profile has been fetched and matched (has revoptimal id) but no personas
   * - Array of strings if profile has been fetched and matched (has revoptimal id) and there are
   *   personas
   */
  decision: string[] | null | undefined;
};

export type UserBusinessProfileDecider = () => UserBusinessProfileDecition;

// Create a reverse map of the display text to the business persona key
const displayTextToBusinessPersonaKeyMap = Object.fromEntries(
  Object.entries({
    ...businessPersonasEmployeeCountDisplayTextMap,
    ...businessPersonasRevenueDisplayTextMap,
    ...businessPersonasPrimaryIndustryDisplayTextMap,
  }).map(([key, value]) => [value, key])
);

/**
 * Hook that returns helper functions for working with a user's business profile.
 *
 * The user business profile functions, when triggerred have side-effects like reading/setting
 * cookies or making RPC requets, so they should be called conditionally on them being needed.
 */
export function useUserBusinessProfile(): UserBusinessProfileDecider {
  const { cookieManager, getProfile, personas } = useContext(AppContext);

  const [needsDecision, setNeedsDecision] = useState(false);

  const { data: profile } = useAsyncData({
    skip: !needsDecision, // Skip loading if we don't need the decision.
    dataId: 'profile', // Has to be the same as in other places to hit cache.
    dataAsync: getProfile,
    onError: error => logger.logError({ component, error }),
  });

  const getUserBusinessProfile = useCallback<UserBusinessProfileDecider>(() => {
    // Check for business personas in app state first
    if (personas?.biz !== undefined) {
      return { isThinking: false, decision: personas.biz };
    }

    const personasCookieValue = getPersonasCookie(cookieManager);

    // If there are business personas in the cookie, return them
    if (personasCookieValue?.biz !== undefined) {
      return { isThinking: false, decision: personasCookieValue.biz };
    }

    // Signals to load the user's business profile - only activate if not found in app state or cookie
    !needsDecision && setNeedsDecision(true);
    return getUserBusinessProfileImpl(profile);
  }, [cookieManager, needsDecision, profile, personas]);

  return getUserBusinessProfile;
}

/**
 * Internal helper function with the business logic.
 *
 * Extracted for testing purposes.
 */
export const getUserBusinessProfileImpl = (
  profileData: ProfileResponse | undefined
): UserBusinessProfileDecition => {
  // Profile not loaded yet. Don't make a decision yet
  if (!profileData) {
    return { isThinking: true, decision: undefined };
  }

  // If the profile has been retrieved, but there is no profile (no revoptimal id)
  if (!profileData?.profile) {
    return { isThinking: false, decision: null };
  }

  // If the profile has been retrieved, return the business personas
  const decision = [];

  // Add each business persona to the decision if found
  const { companyEmployeeCount, companyRevenue, primaryIndustry } = profileData;

  if (companyEmployeeCount) {
    // Convert the display text value from the profile data to the business persona key
    const companyEmployeeCountKey = displayTextToBusinessPersonaKeyMap[companyEmployeeCount];

    if (companyEmployeeCountKey) {
      decision.push(companyEmployeeCountKey);
    }
  }

  if (companyRevenue) {
    // Convert the display text value from the profile data to the business persona key
    const companyRevenueKey = displayTextToBusinessPersonaKeyMap[companyRevenue];

    if (companyRevenueKey) {
      decision.push(companyRevenueKey);
    }
  }

  if (primaryIndustry) {
    // Convert the display text value from the profile data to the business persona key
    const primaryIndustryKey = displayTextToBusinessPersonaKeyMap[primaryIndustry];

    if (primaryIndustryKey) {
      decision.push(primaryIndustryKey);
    }
  }

  return { isThinking: false, decision };
};
